import React, { useState, useEffect } from "react"
import { useAppDispatch, useAppSelector } from "../../../../app/hooks"
import { toggleVobAssignmentAddModal } from "../../../../app/slices/ProfileModuleSlice";
import { Profile } from "../../../../services/profileService/model/profile/Profile";
import { Alert, AlertTitle, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Autocomplete, Chip } from "@mui/material";
import { useAddVobAssignmentMutation, useAllAssignmentTypesQuery, useAllProcessTypesQuery, useAllProfilesQuery } from "../../../../services/profileService/profile-api";
import { VobAssignmentAddCommand, VobAssignmentDto } from "../../../../services/profileService/commands";
import { usePayerListQuery } from "../../../../services/orgService/org-api";
import CenterList from "../../../../services/orgService/model/CenterList";
import { AssignmentType } from "../../../../services/profileService/model/AssignmentType";
import { ProcessType } from "../../../../services/profileService/model/ProcessType";
import { findConflictingAssignments, ConflictingAssignment } from "./vobAssignmentUtils";

type ownProps = {
    profile?: Profile;
    centers: CenterList[];
}

function VobAssignmentAdd(props: ownProps) {
    const modalOpen = useAppSelector((state) => state.profileModule.vobAssignmentAddModal);
    const dispatch = useAppDispatch();
    const [addVobAssignment] = useAddVobAssignmentMutation();
    const { data: payers } = usePayerListQuery('ALL');
    const { data: assignmentTypes } = useAllAssignmentTypesQuery();
    const { data: processTypes } = useAllProcessTypesQuery();
    const { data: allProfiles } = useAllProfilesQuery();

    const [profileToAssign, setProfileToAssign] = useState<Profile | null>(null);
    const [selectedAssignmentType, setSelectedAssignmentType] = useState<AssignmentType | null>(null);
    const [selectedProcessType, setSelectedProcessType] = useState<ProcessType | null>(null);
    const [selectedValues, setSelectedValues] = useState<string[]>([]);
    const [conflicts, setConflicts] = useState<ConflictingAssignment[]>([]);

    useEffect(() => {
        if (props.profile) {
            setProfileToAssign(props.profile);
        }
    }, [props.profile]);

    // Check for conflicting assignments when values change
    useEffect(() => {
        if (selectedAssignmentType && selectedProcessType && selectedValues.length > 0) {
            const targetUpn = profileToAssign?.upn || props.profile?.upn;
            const foundConflicts = findConflictingAssignments(
                allProfiles,
                selectedAssignmentType.code,
                selectedProcessType.code,
                selectedValues,
                targetUpn
            );
            setConflicts(foundConflicts);
        } else {
            setConflicts([]);
        }
    }, [allProfiles, selectedAssignmentType, selectedProcessType, selectedValues, profileToAssign, props.profile]);

    const uniqueFinancialClasses = Array.from(
        new Set(payers?.map(p => p.financialClass).filter(Boolean))
    ).sort();

    const getValueOptions = () => {
        if (!selectedAssignmentType) return [];

        if (selectedAssignmentType.code === 'Center') {
            return props.centers.map(c => ({ value: c.code, label: `${c.code} - ${c.name}` }));
        } else if (selectedAssignmentType.code === 'Payer') {
            return uniqueFinancialClasses.map(fc => ({ value: fc, label: fc }));
        }
        return [];
    }

    const handleSave = async () => {
        if (!profileToAssign || !selectedAssignmentType || !selectedProcessType || selectedValues.length === 0) {
            return;
        }

        const assignmentDto = new VobAssignmentDto(
            selectedValues,
            selectedAssignmentType.code,
            selectedProcessType.code
        );

        const command = new VobAssignmentAddCommand(
            profileToAssign.upn,
            [assignmentDto]
        );

        try {
            await addVobAssignment(command).unwrap();
            handleClose();
        } catch (error) {
            console.error('Failed to add assignment:', error);
        }
    }

    const handleClose = () => {
        if (!props.profile) {
            setProfileToAssign(null);
        }
        setSelectedAssignmentType(null);
        setSelectedProcessType(null);
        setSelectedValues([]);
        setConflicts([]);
        dispatch(toggleVobAssignmentAddModal());
    }

    // Helper to get display name for profile
    const getProfileDisplayName = (profile: Profile): string => {
        if (profile.identity && profile.identity.trim() !== '') {
            return profile.identity;
        }
        if (profile.fullName && profile.fullName.trim() !== '' && profile.fullName !== ', ') {
            return `${profile.fullName} (${profile.upn})`;
        }
        return profile.email || profile.upn;
    };

    return <React.Fragment>
        <Dialog open={modalOpen} maxWidth='md' fullWidth>
            <DialogTitle>Add Profile Assignment</DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{ mt: 2 }}>
                    {!props.profile && (
                        <Autocomplete
                            options={allProfiles || []}
                            getOptionLabel={(option: Profile) => getProfileDisplayName(option)}
                            value={profileToAssign}
                            onChange={(event, value) => setProfileToAssign(value)}
                            renderInput={(params) => (
                                <TextField {...params} label="Select User" variant="outlined" />
                            )}
                            isOptionEqualToValue={(option, value) => option.upn === value.upn}
                            fullWidth
                        />
                    )}

                    {props.profile && (
                        <TextField
                            label="User"
                            value={getProfileDisplayName(props.profile)}
                            fullWidth
                            disabled
                        />
                    )}

                    {(profileToAssign || props.profile) && (
                        <>
                            <Autocomplete
                                options={assignmentTypes || []}
                                getOptionLabel={(option: AssignmentType) => option.name}
                                value={selectedAssignmentType}
                                onChange={(event, value) => {
                                    setSelectedAssignmentType(value);
                                    setSelectedValues([]);
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} label="Assignment Type" variant="outlined" />
                                )}
                                fullWidth
                            />

                            <Autocomplete
                                options={processTypes || []}
                                getOptionLabel={(option: ProcessType) => option.name}
                                value={selectedProcessType}
                                onChange={(event, value) => setSelectedProcessType(value)}
                                renderInput={(params) => (
                                    <TextField {...params} label="Process Type" variant="outlined" />
                                )}
                                fullWidth
                            />

                            {selectedAssignmentType && (
                                <Autocomplete
                                    multiple
                                    options={getValueOptions()}
                                    getOptionLabel={(option) => option.label}
                                    value={getValueOptions().filter(opt => selectedValues.includes(opt.value))}
                                    onChange={(event, values) => setSelectedValues(values.map(v => v.value))}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={selectedAssignmentType.code === 'Center' ? 'Select Centers' : 'Select Payers'}
                                            variant="outlined"
                                        />
                                    )}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip label={option.label} {...getTagProps({ index })} />
                                        ))
                                    }
                                    fullWidth
                                />
                            )}

                            {conflicts.length > 0 && (
                                <Alert severity="warning">
                                    <AlertTitle>Assignment Conflicts</AlertTitle>
                                    {conflicts.map((c, i) => (
                                        <div key={i}>'{c.userName}' is currently assigned to '{c.value}' for '{selectedAssignmentType?.name}'</div>
                                    ))}
                                </Alert>
                            )}
                        </>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Stack direction='row' justifyContent='flex-end' spacing={2}>
                    <Button variant='outlined' onClick={handleClose}>Cancel</Button>
                    <Button
                        variant='contained'
                        onClick={handleSave}
                        disabled={!profileToAssign || !selectedAssignmentType || !selectedProcessType || selectedValues.length === 0}
                    >
                        Save
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    </React.Fragment>
}

export default VobAssignmentAdd;
